<template>
  <div class="form-container">
    <!-- Name -->
    <div class="form-item col-name">
      <span v-if="!isEditing" class="input-text">{{ name }}</span>
      <sprinput
        v-else
        v-model="formData.name"
        class="input-text"
        :hint="$t('externalForm.link')"
        :size="'5'"
        :placeholder="$t('externalForm.name')"
      />
    </div>

    <!-- Link -->
    <div class="form-item col-link">
      <a v-if="!isEditing" class="input-text" :href="link" target="_blank">{{
        link
      }}</a>
      <sprinput
        v-else
        v-model="formData.link"
        class="input-text"
        :size="'5'"
        :hint="$t('externalForm.link')"
        :placeholder="$t('externalForm.link')"
      />
    </div>

    <!-- Action Icons -->
    <div class="form-item action-icons">
      <a
        v-show="!isEditing"
        class="action-icon"
        tabindex="0"
        href="javascript:void(0);"
        @click="toggleEdit"
      >
        <font-awesome-icon icon="pencil-alt" aria-hidden="true" />
      </a>
      <a
        v-show="!isEditing"
        class="action-icon"
        tabindex="0"
        href="javascript:void(0);"
        @click="deleteForm"
      >
        <font-awesome-icon icon="trash" aria-hidden="true" />
      </a>
      <a
        v-show="isEditing"
        class="action-icon"
        tabindex="0"
        href="javascript:void(0);"
        @click="toggleEdit"
      >
        <font-awesome-icon icon="check" class="save" aria-hidden="true" />
      </a>
      <a
        v-show="isEditing"
        class="action-icon"
        tabindex="0"
        href="javascript:void(0);"
        @click="revertChanges"
      >
        <font-awesome-icon icon="times" aria-hidden="true" />
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import sprinput from '../SPRInput.vue'
import { ExternalFormUpdateDto } from '@/types/index'

export interface TemplateComponentData {
  isEditing: boolean
  formData: ExternalFormUpdateDto
}

export default Vue.extend({
  components: { sprinput },
  props: {
    name: { type: String, default: '' },
    link: { type: String, default: '' },
    isNew: { type: Boolean, default: false },
    id: { type: Number, default: 0 },
  },
  data() {
    return {
      isEditing: this.isNew,
      formData: {
        id: this.id,
        name: this.name,
        link: this.link,
      } as ExternalFormUpdateDto,
      updatedFormData: {
        id: this.id,
        name: '',
        link: '',
      } as ExternalFormUpdateDto,
      backupName: '',
      backupLink: '',
    }
  },
  methods: {
    edit() {
      this.isEditing = true
    },
    cancel() {
      this.$emit('cancel')
    },
    submitForm() {
      this.$emit('save', this.formData)
    },
    update() {
      this.updatedFormData = { ...this.formData }
      try {
        let url =
          this.$store.state.apiBases.userservice +
          this.$store.state.apiEndpoints.externalformsupdate[0]
        url = url.replace('{0}', this.id)
        this.$http.put(url, this.updatedFormData).then((response) => {
          if (response.ok) {
            this.backupLink = this.updatedFormData.link
            this.backupName = this.updatedFormData.name
            // @ts-ignore
            this.showAlertSuccess(`${this.$t('common.settingUpdated')}`)
            this.$emit('saved')
          }
        })
      } catch (error) {
        console.error('Error updating external form:', error)
      }
    },
    revertChanges: function () {
      if (this.isEditing) {
        this.formData.name = this.backupName
        this.formData.link = this.backupLink
        this.toggleEdit()
      }
      if (this.isNew) {
        this.$emit('cancel')
      }
    },
    deleteForm() {
      this.$emit('delete', this.formData.id)
    },
    toggleEdit: function () {
      this.isEditing = !this.isEditing

      if (this.isEditing) {
        this.backupLink = this.formData.link
        this.backupName = this.formData.name
      } else {
        if (
          this.backupLink !== this.formData.link ||
          this.backupName !== this.formData.name
        ) {
          if (this.isNew) {
            this.submitForm()
          } else {
            this.update()
          }
        }
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/_variables.scss';
.form-container {
  display: flex;
  flex-wrap: nowrap;       /* prevent individual wrapping */
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 25px;
}
/* Wrap all items together on small screens */
@media (max-width: 990px) {
  .form-container {
    flex-direction: column;  /* stack everything */
    align-items: stretch;    /* optional, make inputs full width */
  }
}
.input-container {
  display: flex;
  flex-grow: 1;
}
.container > div {
  flex: 1 1 250px;
  min-width: 200px;
}
.form-item {
  flex: 1;          /* take available space equally */
  min-width: 200px; /* optional: sets the limit before stacking */
}
.input-text {
  flex: 1;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.input-button {
  margin-left: 10px; /* Adjust the space between the inputs and buttons if needed */
}
.action-icons {
  display: flex;
  padding-left: 5%;
  gap: 0.5rem;
  flex: 0 0 auto;
}
.action-icon {
  display: flex;
  align-items: center;
  color: $brand-red;
  align-items: center;
  font-family: $font-bold;
  cursor: pointer;
  text-decoration: none;
  text-transform: uppercase;
  padding-right: 5px;

  .save {
    color: $brand-success;
  }
}
@media (max-width: 990px) {
  .action-icons {
    flex: 1 1 100%;
    justify-content: flex-start;
    padding-left: 0;
  }
}
</style>
